import { useEffect, useMemo, useState } from "react";
import { FileDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { generateHariPDF } from "@/lib/pdf/generate-hari";
import { downloadPDF } from "@/lib/pdf/pdf-shared";
import { HARI_PENTING } from "@/data/hari-penting";
import { MONTH_NAMES_ID } from "@/lib/calendar/dates";

interface Props {
  /** Pre-checked slugs (e.g. only the current detail page). */
  defaultSlugs?: string[];
  /** Hide the slug picker (preset mode). */
  presetOnly?: boolean;
  buttonLabel?: string;
  /** Show extra "Rekap" modes (range/month/year). */
  showRekap?: boolean;
}

export function HariPdfButton({ defaultSlugs, presetOnly, buttonLabel = "Download PDF", showRekap = false }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoMode, setLogoMode] = useState<"default" | "custom" | "none">("default");
  const [selected, setSelected] = useState<string[]>(defaultSlugs ?? []);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const nowYear = new Date().getFullYear();
  const [mode, setMode] = useState<"pilih" | "rentang" | "bulan" | "tahun">("pilih");
  const [rangeFrom, setRangeFrom] = useState<string>(`${nowYear}-01-01`);
  const [rangeTo, setRangeTo] = useState<string>(`${nowYear}-12-31`);
  const [pickMonth, setPickMonth] = useState<number>(new Date().getMonth() + 1);
  const [pickYear, setPickYear] = useState<number>(nowYear);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = window.localStorage.getItem("pdf-logo-mode");
    if (saved === "default" || saved === "custom" || saved === "none") setLogoMode(saved);
  }, []);
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("pdf-logo-mode", logoMode);
  }, [logoMode]);

  // Reset preview when selection or logo changes.
  useEffect(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.join(","), logoMode, logo, mode, rangeFrom, rangeTo, pickMonth, pickYear]);

  const grouped = useMemo(() => {
    const map: Record<number, typeof HARI_PENTING> = {};
    for (const h of HARI_PENTING) {
      const m = parseInt(h.monthDay.slice(0, 2), 10);
      (map[m] ||= []).push(h);
    }
    return map;
  }, []);

  const slugList = presetOnly ? (defaultSlugs ?? []) : selected;

  const effectiveRange = useMemo(() => {
    if (mode === "rentang") return { from: rangeFrom, to: rangeTo, label: `${rangeFrom} sampai ${rangeTo}` };
    if (mode === "bulan") {
      const last = new Date(pickYear, pickMonth, 0).getDate();
      return {
        from: `${pickYear}-${String(pickMonth).padStart(2, "0")}-01`,
        to: `${pickYear}-${String(pickMonth).padStart(2, "0")}-${String(last).padStart(2, "0")}`,
        label: `${MONTH_NAMES_ID[pickMonth - 1]} ${pickYear}`,
      };
    }
    if (mode === "tahun") {
      return { from: `${pickYear}-01-01`, to: `${pickYear}-12-31`, label: `Tahun ${pickYear}` };
    }
    return null;
  }, [mode, rangeFrom, rangeTo, pickMonth, pickYear]);

  function toggle(slug: string) {
    setSelected((s) => (s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]));
  }

  function selectAll() {
    setSelected(HARI_PENTING.map((h) => h.slug));
  }
  function clearAll() {
    setSelected([]);
  }

  async function buildBytes() {
    const useDefaultLogo = logoMode === "default";
    const logoFile = logoMode === "custom" ? logo : null;
    if (effectiveRange && !presetOnly) {
      return await generateHariPDF({
        dateRange: { from: effectiveRange.from, to: effectiveRange.to },
        periodLabel: effectiveRange.label,
        logo: logoFile,
        useDefaultLogo,
        year: nowYear,
        siteUrl: "https://hari.co.id/hari/",
      });
    }
    return await generateHariPDF({
      slugs: slugList,
      logo: logoFile,
      useDefaultLogo,
      year: nowYear,
      siteUrl: "https://hari.co.id/hari/",
    });
  }

  const canBuild = effectiveRange !== null || slugList.length > 0;

  async function handlePreview() {
    if (!canBuild) {
      toast.error("Pilih minimal satu hari atau rentang.");
      return;
    }
    setBusy(true);
    try {
      const bytes = await buildBytes();
      const blob = new Blob([bytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err) {
      console.error(err);
      toast.error("Gagal membuat preview PDF.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDownload() {
    if (!canBuild) {
      toast.error("Pilih minimal satu hari atau rentang.");
      return;
    }
    setBusy(true);
    try {
      const bytes = await buildBytes();
      const name = effectiveRange
        ? `hari-rekap-${effectiveRange.from}-${effectiveRange.to}.pdf`
        : (slugList.length === 1 ? `hari-${slugList[0]}-${nowYear}.pdf` : `hari-penting-${nowYear}.pdf`);
      downloadPDF(bytes, name);
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Gagal membuat PDF.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o && previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <FileDown className="mr-2 h-4 w-4" /> {buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Download PDF Hari Penting</DialogTitle>
          <DialogDescription>
            {presetOnly
              ? `Export ${slugList.length} hari peringatan terpilih ke PDF.`
              : "Pilih hari peringatan yang ingin di-export, atur logo, lalu preview sebelum download."}
          </DialogDescription>
        </DialogHeader>

        {!presetOnly && showRekap && (
          <div>
            <Label className="mb-2 block">Mode rekap</Label>
            <RadioGroup value={mode} onValueChange={(v) => setMode(v as typeof mode)} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-xs">
                <RadioGroupItem value="pilih" /> Pilih hari
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-xs">
                <RadioGroupItem value="rentang" /> Rentang tanggal
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-xs">
                <RadioGroupItem value="bulan" /> Per bulan
              </Label>
              <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-xs">
                <RadioGroupItem value="tahun" /> Per tahun
              </Label>
            </RadioGroup>
            {mode === "rentang" && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="rfrom" className="text-xs">Dari</Label>
                  <Input id="rfrom" type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="rto" className="text-xs">Sampai</Label>
                  <Input id="rto" type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} />
                </div>
              </div>
            )}
            {mode === "bulan" && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="pm" className="text-xs">Bulan</Label>
                  <select id="pm" className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={pickMonth} onChange={(e) => setPickMonth(parseInt(e.target.value, 10))}>
                    {MONTH_NAMES_ID.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="py" className="text-xs">Tahun</Label>
                  <Input id="py" type="number" value={pickYear} onChange={(e) => setPickYear(parseInt(e.target.value || `${nowYear}`, 10))} />
                </div>
              </div>
            )}
            {mode === "tahun" && (
              <div className="mt-2">
                <Label htmlFor="py2" className="text-xs">Tahun</Label>
                <Input id="py2" type="number" value={pickYear} onChange={(e) => setPickYear(parseInt(e.target.value || `${nowYear}`, 10))} />
              </div>
            )}
          </div>
        )}

        {!presetOnly && (mode === "pilih" || !showRekap) && (
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="block">Pilih hari ({selected.length} dipilih)</Label>
              <div className="flex gap-2 text-xs">
                <button type="button" className="text-primary hover:underline" onClick={selectAll}>Pilih semua</button>
                <button type="button" className="text-muted-foreground hover:underline" onClick={clearAll}>Kosongkan</button>
              </div>
            </div>
            <ScrollArea className="h-56 rounded-md border border-border p-3">
              <div className="space-y-3">
                {Object.keys(grouped).map((mStr) => {
                  const m = parseInt(mStr, 10);
                  return (
                    <div key={m}>
                      <p className="text-xs font-semibold text-muted-foreground">{MONTH_NAMES_ID[m - 1]}</p>
                      <ul className="mt-1 space-y-1">
                        {grouped[m].map((h) => {
                          const dd = parseInt(h.monthDay.slice(3), 10);
                          const checked = selected.includes(h.slug);
                          return (
                            <li key={h.slug} className="flex items-center gap-2 text-sm">
                              <Checkbox id={`hp-${h.slug}`} checked={checked} onCheckedChange={() => toggle(h.slug)} />
                              <Label htmlFor={`hp-${h.slug}`} className="cursor-pointer font-normal">
                                <span className="text-muted-foreground">{dd}</span> {h.name}
                              </Label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}

        <div>
          <Label className="mb-2 block">Logo</Label>
          <RadioGroup value={logoMode} onValueChange={(v) => setLogoMode(v as typeof logoMode)} className="grid gap-2">
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="default" /> Logo Hari.co.id (default)
            </Label>
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="custom" /> Upload logo sendiri
            </Label>
            <Label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-2 text-sm">
              <RadioGroupItem value="none" /> Tanpa logo
            </Label>
          </RadioGroup>
          {logoMode === "custom" && (
            <Input
              id="logo-hari"
              type="file"
              accept="image/png,image/jpeg"
              className="mt-2"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                if (f && f.size > 1024 * 1024) {
                  toast.error("Logo terlalu besar (maks 1MB).");
                  return;
                }
                setLogo(f);
              }}
            />
          )}
        </div>

        {previewUrl && (
          <div>
            <Label className="mb-2 block">Preview</Label>
            <iframe
              title="Preview PDF Hari Penting"
              src={previewUrl}
              className="h-72 w-full rounded-md border border-border"
            />
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
          <Button variant="outline" onClick={handlePreview} disabled={busy || !canBuild}>
            {busy && !previewUrl ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Preview
          </Button>
          <Button onClick={handleDownload} disabled={busy || !canBuild || (logoMode === "custom" && !logo)}>
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Menyiapkan...</> : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}